import * as Sentry from '@sentry/nextjs';
import throttle from 'lodash/throttle';
import Head from 'next/head';
import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  type Middleware,
  type Placement,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import { X } from '@phosphor-icons/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTheme } from 'styled-components';

import { getFloatingPortalRoot, toggleTrustBadge } from '@tmh-website/services/floating-ui.service';
import { isiOS } from '@tmh-website/services/utils.service';
import { DropdownMenuContext, useIsMobile } from '@tmh-website/shared';
import { type DropdownThemeColor, dropdownThemes, StyledDropdown } from '@tmh-website/ui/Dropdown';
import { StyledFormElements } from '@tmh-website/ui/FormElements';
import { useForwardRef } from '@tmh-website/shared/hooks';

import { virtualScrollMinChildCount } from '../Dropdown/Dropdown.styles';

export interface DropdownMenuProps {
  /**
   * Anchor element to toggle this menu.
   */
  anchorEl: ReactElement;
  defaultPlacement?: Placement;
  children?: ReactNode;
  closeOnClick?: boolean;
  /**
   * Adds a search field to the dropdown.
   */
  hasSearchInput?: boolean;
  onSearchInputChange?: (e: string) => void;
  themeColor: DropdownThemeColor;
  disabled?: boolean;
  customOffset?: number;
  /**
   * Completely overwrites floating-ui middleware config.
   */
  customMiddleware?: Middleware[];
  customContainerProps?: Record<string, any>;
  noPadding?: boolean;
  virtualScrollContainerProps?: HTMLAttributes<HTMLDivElement> & {
    [key: `data-${string}`]: string;
  };

  /**
   * Optional handler for the changes in the `isOpen` state.
   *
   * @param newValue - The current/new value of the `isOpen` state.
   * @param oldValue - The previous/old value of the `isOpen` state.
   */
  onOpenChange?: (newValue: boolean, oldValue: boolean) => unknown;
}

const DropdownMenu = forwardRef<HTMLSelectElement, DropdownMenuProps>(
  (
    {
      anchorEl,
      children,
      closeOnClick = true,
      customContainerProps,
      customMiddleware,
      customOffset,
      defaultPlacement = 'bottom-start',
      disabled = false,
      hasSearchInput,
      noPadding,
      onOpenChange,
      onSearchInputChange,
      themeColor,
      virtualScrollContainerProps = {},
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [isInDOM, setIsInDOM] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isScrollEnd, setIsScrollEnd] = useState<('top' | 'bottom')[]>(['top']);
    const [entryHeight, setEntryHeight] = useState(40);
    const [disableOnNavigate, setDisableOnNavigate] = useState(false);
    const virtualScrollContainerRef = useRef<HTMLDivElement>(null);
    const listItemsRef = useRef<Array<HTMLButtonElement | null>>([]);
    const childCount = Children.count(children);
    const hasVirtualScroll = childCount > virtualScrollMinChildCount;
    const uniqueId = useId();
    const isMobile = useIsMobile();
    const { _forceCloseCounter } = useContext(DropdownMenuContext);
    const inputRef = useForwardRef<HTMLSelectElement>(ref);
    const { textOpacity } = useTheme();
    /**
     * FloatingUI setup
     */
    const { x, y, reference, floating, strategy, context, placement } = useFloating<HTMLButtonElement>({
      open: isOpen,
      onOpenChange: newOpenState => {
        if ((childCount === 0 && newOpenState === true) || disabled) {
          // Avoid opening the dropdown if there are no children
          return;
        }

        setIsOpen(newOpenState);

        if (typeof onOpenChange === 'function') {
          onOpenChange(newOpenState, !newOpenState);
        }
      },
      middleware: customMiddleware ?? [offset({ mainAxis: customOffset ?? 4 }), flip(), shift()],
      placement: defaultPlacement,
      whileElementsMounted: autoUpdate,
    });

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
      useFocus(context),
      useClick(context, { toggle: true }),
      useRole(context, { role: 'menu' }),
      useDismiss(context, { outsidePressEvent: 'click' }),
      useListNavigation(context, {
        listRef: listItemsRef,
        activeIndex,
        onNavigate: index => {
          if (disableOnNavigate) {
            return;
          }
          setActiveIndex(index);
        },
      }),
    ]);
    /**
     * Set transform origin for css transition
     */
    let transformOriginX, transformOriginY;
    if (placement.includes('start')) {
      transformOriginX = 'left';
    } else if (placement.includes('end')) {
      transformOriginX = 'right';
    } else {
      transformOriginX = 'center';
    }

    if (placement.includes('top')) {
      transformOriginY = 'bottom';
    } else if (placement.includes('bottom')) {
      transformOriginY = 'top';
    } else {
      transformOriginY = 'center';
    }
    /**
     * Toggle dropdown with a delay to enable css transitions and hide trustbadge on mobile
     */
    useEffect(() => {
      if (isMobile) {
        toggleTrustBadge(!isOpen);
      }

      let timeout: number;

      if (isOpen) {
        timeout = window.setTimeout(() => {
          setIsInDOM(true);
          setIsVisible(true);
        }, 10);
      } else {
        setIsVisible(false);
        timeout = window.setTimeout(() => {
          setIsInDOM(false);
          if (onSearchInputChange) {
            // Clear the search input delayed to avoid layout shift
            onSearchInputChange('');
          }
        }, 300);
      }

      return () => {
        window.clearTimeout(timeout);
      };
    }, [isOpen]);

    if (!isValidElement(anchorEl)) {
      const error = new Error('Invalid anchor element provided.');
      Sentry.captureException(error);
      throw error;
    }

    /**
     * Create children
     */
    const createChild = useCallback((child, index) => {
      return (
        isValidElement(child) &&
        cloneElement(
          child,
          getItemProps({
            role: 'menuitem',
            ref(node: HTMLButtonElement) {
              listItemsRef.current[index] = node;
            },
            onClick() {
              if (closeOnClick) {
                setIsOpen(false);
              }
            },
          })
        )
      );
    }, []);

    /**
     * Update entry height to create correct placeholder entries in virtual scroll
     */
    useEffect(() => {
      if (
        !isVisible ||
        !listItemsRef.current ||
        !listItemsRef.current[0] ||
        !listItemsRef.current[1] ||
        !listItemsRef.current[2]
      ) {
        return;
      }
      const averageHeight =
        (listItemsRef.current[0]?.scrollHeight +
          listItemsRef.current[1]?.scrollHeight +
          listItemsRef.current[2]?.scrollHeight) /
        3;
      setEntryHeight(averageHeight);
    }, [isVisible, childCount, isMobile]);

    /**
     * Check if user has scrolled to top or bottom to hide fades
     */
    function checkScrollPosition() {
      const currentEl = virtualScrollContainerRef.current;
      if (!currentEl) {
        return;
      }
      const newValue: ('top' | 'bottom')[] = [];
      if (currentEl.scrollTop < 20) {
        newValue.push('top');
      }
      if (currentEl.scrollHeight - currentEl.scrollTop - currentEl.clientHeight < 20) {
        newValue.push('bottom');
      }
      setIsScrollEnd(newValue);
    }
    useEffect(() => {
      const currentEl = virtualScrollContainerRef.current;
      if (currentEl && isInDOM) {
        checkScrollPosition();
        const throttledListener = throttle(checkScrollPosition, 250);
        currentEl.addEventListener('scroll', throttledListener);
        return () => currentEl.removeEventListener('scroll', throttledListener);
      }
      return () => {};
    }, [virtualScrollContainerRef.current, isInDOM]);

    // Focus search input upon open
    useEffect(() => {
      if (isVisible && inputRef) {
        inputRef.current?.focus();
      }
    }, [isVisible]);

    useEffect(() => {
      if (_forceCloseCounter > 0) {
        setIsOpen(false);
      }
    }, [_forceCloseCounter]);

    const anchorProps = getReferenceProps({ ref: reference, open: isOpen, 'aria-expanded': isOpen ? true : undefined });

    const virtualizer = useVirtualizer({
      count: childCount,
      getScrollElement: () => virtualScrollContainerRef.current,
      estimateSize: () => entryHeight ?? 40,
      overscan: 10,
      paddingStart: isMobile ? 4 : 8,
      paddingEnd: isMobile ? 4 : 8,
      initialRect: { height: 320, width: 194 },
    });
    const items = virtualizer.getVirtualItems();

    return (
      <>
        {cloneElement(anchorEl, {
          ...anchorProps,
          ...customContainerProps,
        })}
        <FloatingPortal root={getFloatingPortalRoot()}>
          {(isOpen || isInDOM) && (
            <FloatingFocusManager context={context} modal order={['reference', 'content']} returnFocus>
              <>
                {/**
                 * iOS's devices will try to zoom in on inputs when they are focused. We prevent this by adding this meta tag.
                 * Always adding this will result in a18y issues on Android
                 */}
                {isiOS() && (
                  <Head>
                    <meta
                      name='viewport'
                      content='width=device-width, initial-scale=1, maximum-scale=1'
                      key='ios-zoom-fix'
                    />
                  </Head>
                )}
                <StyledDropdown.Container
                  $isVisible={isVisible}
                  $themeColor={themeColor}
                  $noPaddingTop={noPadding || hasVirtualScroll || !!hasSearchInput}
                  $noPaddingBottom={noPadding || hasVirtualScroll}
                  {...getFloatingProps({
                    ref: floating,
                    style: {
                      position: strategy,
                      top: y ?? 0,
                      left: x ?? 0,
                      transformOrigin: `${transformOriginX} ${transformOriginY}`,
                    },
                  })}>
                  {hasSearchInput && onSearchInputChange && (
                    <form
                      onSubmit={() => {
                        // Select the first item on enter. This is very sketchy, but the only feasible solution
                        if (listItemsRef.current && listItemsRef.current[0]) {
                          listItemsRef.current[0].click();
                        }
                      }}>
                      <StyledDropdown.SearchInputContainer
                        $themeColor={dropdownThemes[themeColor].searchInputThemeColor}
                        $dropdownThemeColor={themeColor}
                        $hasError={false}
                        $isDisabled={false}>
                        <StyledFormElements.InputLabel
                          $themeColor={dropdownThemes[themeColor].searchInputThemeColor}
                          $fieldIsFilled={!!inputRef.current && inputRef.current.value !== ''}
                          htmlFor={`${uniqueId}_search`}>
                          {t('global:search.label')}
                        </StyledFormElements.InputLabel>
                        <StyledFormElements.Input
                          name={`${uniqueId}_search`}
                          id={`${uniqueId}_search`}
                          $themeColor={dropdownThemes[themeColor].searchInputThemeColor}
                          $smallLabel={!!inputRef.current && inputRef.current.value !== ''}
                          $smallPaddingRight
                          onChange={e => onSearchInputChange(e.target.value)}
                          onFocus={() => setDisableOnNavigate(true)}
                          onBlur={() => setDisableOnNavigate(false)}
                          ref={inputRef}
                        />
                        <StyledFormElements.ClearButton
                          as='button'
                          type='button'
                          onClick={() => {
                            if (inputRef.current) {
                              inputRef.current.value = '';
                            }
                            onSearchInputChange('');
                          }}
                          $isVisible={!!inputRef.current && inputRef.current.value !== ''}>
                          <X size={16} weight='bold' />
                        </StyledFormElements.ClearButton>
                      </StyledDropdown.SearchInputContainer>

                      {Children.count(children) === 0 && (
                        <StyledDropdown.ErrorMessage
                          $style='uiTextSmall'
                          as='p'
                          $opacity={textOpacity}
                          $color={dropdownThemes[themeColor].text}>
                          {t('global:search.noResults')}
                        </StyledDropdown.ErrorMessage>
                      )}
                    </form>
                  )}
                  {hasVirtualScroll ? (
                    <StyledDropdown.VirtualScrollContainer
                      $noPaddingTop={!!hasSearchInput}
                      $themeColor={themeColor}
                      $isScrollEnd={isScrollEnd}
                      $hasSearch={!!hasSearchInput}
                      ref={virtualScrollContainerRef}
                      {...virtualScrollContainerProps}>
                      <StyledDropdown.VirtualScrollTotalHeightContainer $height={virtualizer.getTotalSize()}>
                        <StyledDropdown.VirtualScrollPositionContainer
                          style={{ transform: `translateY(${items[0]?.start ?? 0}px)` }}>
                          {virtualizer
                            .getVirtualItems()
                            .map(virtualItem =>
                              createChild(Children.toArray(children)[virtualItem.index], virtualItem.index)
                            )}
                        </StyledDropdown.VirtualScrollPositionContainer>
                      </StyledDropdown.VirtualScrollTotalHeightContainer>
                    </StyledDropdown.VirtualScrollContainer>
                  ) : (
                    Children.map(children, (child, index) => createChild(child, index))
                  )}
                </StyledDropdown.Container>
                <StyledDropdown.BackgroundFade $isVisible={isVisible} />
              </>
            </FloatingFocusManager>
          )}
        </FloatingPortal>
      </>
    );
  }
);

export default DropdownMenu;
